"use client";

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Field } from "../types/types";

interface ConflictData {
  localVersion: number;
  storageVersion: number;
  localAnswers: Record<string, any>;
  storageAnswers: Record<string, any>;
}

interface ConflictResolutionModalProps {
  isOpen: boolean;
  conflictData: ConflictData | null;
  formFields?: Field[];
  onClose: () => void;
  onKeepLocal: () => void;
  onUseStorage: () => void;
  onSmartMerge: () => void;
}

export function ConflictResolutionModal({
  isOpen,
  conflictData,
  formFields = [],
  onClose,
  onKeepLocal,
  onUseStorage,
  onSmartMerge,
}: ConflictResolutionModalProps) {
  if (!conflictData) return null;

  // Calculate conflicting fields
  const conflictingFields: string[] = [];
  Object.keys(conflictData.localAnswers).forEach(fieldId => {
    const localValue = conflictData.localAnswers[fieldId];
    const storageValue = conflictData.storageAnswers[fieldId];

    if (storageValue !== undefined && localValue !== storageValue) {
      conflictingFields.push(fieldId);
    }
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Form Updated in Another Tab
          </DialogTitle>
          <DialogDescription>
            This form was updated in another browser tab. How would you like to handle the changes?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Conflict Summary */}
          <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-yellow-800">
                  {conflictingFields.length} Conflicting Change{conflictingFields.length !== 1 ? 's' : ''} Detected
                </h4>
                <p className="text-sm text-yellow-700 mt-1">
                  This tab (version {conflictData.localVersion}) and another tab (version {conflictData.storageVersion}) both have unsaved changes to the same fields.
                </p>
              </div>
            </div>
          </div>

          {/* Field-level conflicts */}
          {conflictingFields.length > 0 && (
            <div className="max-h-60 overflow-y-auto space-y-3">
              {conflictingFields.map(fieldId => {
                const localValue = conflictData.localAnswers[fieldId];
                const storageValue = conflictData.storageAnswers[fieldId];

                const field = formFields.find(f => f.id === fieldId);
                const fieldLabel = field?.label || fieldId;

                return (
                  <div key={fieldId} className="border rounded-lg p-3 bg-gray-50">
                    <p className="text-sm font-medium text-gray-900 mb-2">{fieldLabel}</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-white rounded border border-blue-200 p-2">
                        <span className="text-xs text-gray-500 font-medium">This Tab:</span>
                        <p className="font-medium text-gray-900 mt-1 break-words">
                          {String(localValue || '(empty)')}
                        </p>
                      </div>
                      <div className="bg-white rounded border border-green-200 p-2">
                        <span className="text-xs text-gray-500 font-medium">Other Tab:</span>
                        <p className="font-medium text-gray-900 mt-1 break-words">
                          {String(storageValue || '(empty)')}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Resolution Options Explanation */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Resolution Options:</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="font-medium text-blue-600 mt-0.5">•</span>
                <span><strong>Keep My Changes:</strong> Discard changes from the other tab and keep your current data</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-medium text-green-600 mt-0.5">•</span>
                <span><strong>Use Other Tab:</strong> Discard your changes and use data from the other tab</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-medium text-purple-600 mt-0.5">•</span>
                <span><strong>Smart Merge:</strong> Keep your changes for conflicting fields, add new fields from other tab</span>
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onKeepLocal}
            className="border-blue-200 hover:bg-blue-50"
          >
            Keep My Changes
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onUseStorage}
            className="border-green-200 hover:bg-green-50"
          >
            Use Other Tab
          </Button>
          <Button
            type="button"
            onClick={onSmartMerge}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            Smart Merge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
